import React, { useState, useEffect } from 'react';
import { apiKeysAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';
import {
  BarChart3,
  Key,
  Activity,
  CheckCircle,
  Clock
} from 'lucide-react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  BarChart,
  Bar
} from 'recharts';

const Dashboard = () => {
  const { user } = useAuth();
  const [usageStats, setUsageStats] = useState(null);
  const [apiKeys, setApiKeys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedPeriod, setSelectedPeriod] = useState(7);

  const fetchData = React.useCallback(async () => {
    try {
      const [usageResponse, keysResponse] = await Promise.all([
        apiKeysAPI.getUsageStats(selectedPeriod),
        apiKeysAPI.getAll()
      ]);

      setUsageStats(usageResponse.data.data);
      setApiKeys(keysResponse.data.data.apiKeys);
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    } finally {
      setLoading(false);
    }
  }, [selectedPeriod]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const formatChartData = () => {
    if (!usageStats?.dailyUsage) return [];

    const dailyData = {};
    usageStats.dailyUsage.forEach(item => {
      const date = item.request_date;
      if (!dailyData[date]) {
        dailyData[date] = {
          date,
          requests: 0,
          success: 0,
          errors: 0
        };
      }
      dailyData[date].requests += item.request_count;
      dailyData[date].success += item.success_count;
      dailyData[date].errors += item.error_count;
    });

    return Object.values(dailyData).sort((a, b) => new Date(a.date) - new Date(b.date));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  const chartData = formatChartData();
  const totalStats = usageStats?.totalStats || {};

  return (
    <div className="max-w-7xl mx-auto">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 flex items-center">
              <BarChart3 className="w-8 h-8 mr-3 text-blue-600" />
              Dashboard
            </h1>
            <p className="text-gray-600 mt-2">
              Welcome back, {user?.username || user?.email}! Here's your API usage overview.
            </p>
          </div>
          
          {/* Period Selector */}
          <div className="flex items-center space-x-2">
            <label className="text-sm font-medium text-gray-700">Period:</label>
            <select
              value={selectedPeriod}
              onChange={(e) => setSelectedPeriod(parseInt(e.target.value))}
              className="rounded border-gray-300 text-sm focus:border-blue-500 focus:ring-blue-500"
            >
              <option value={7}>Last 7 days</option>
              <option value={14}>Last 14 days</option>
              <option value={30}>Last 30 days</option>
            </select>
          </div>
        </div>
      </div>

      {/* Key Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <div className="card p-6">
          <div className="flex items-center">
            <div className="p-3 rounded-full bg-blue-100">
              <Activity className="w-6 h-6 text-blue-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Total Requests</p>
              <p className="text-2xl font-bold text-gray-900">{totalStats.total_requests || 0}</p>
              <p className="text-xs text-gray-500">Last {selectedPeriod} days</p>
            </div>
          </div>
        </div>

        <div className="card p-6">
          <div className="flex items-center">
            <div className="p-3 rounded-full bg-green-100">
              <CheckCircle className="w-6 h-6 text-green-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Success Rate</p>
              <p className="text-2xl font-bold text-gray-900">
                {totalStats.total_requests 
                  ? Math.round((totalStats.total_success / totalStats.total_requests) * 100)
                  : 0}%
              </p>
              <p className="text-xs text-gray-500">{totalStats.total_success || 0} successful</p>
            </div>
          </div>
        </div>

        <div className="card p-6">
          <div className="flex items-center">
            <div className="p-3 rounded-full bg-purple-100">
              <Clock className="w-6 h-6 text-purple-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Avg Response</p>
              <p className="text-2xl font-bold text-gray-900">
                {Math.round(totalStats.avg_response_time || 0)}ms
              </p>
              <p className="text-xs text-gray-500">Response time</p>
            </div>
          </div>
        </div>

        <div className="card p-6">
          <div className="flex items-center">
            <div className="p-3 rounded-full bg-orange-100">
              <Key className="w-6 h-6 text-orange-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Active API Keys</p>
              <p className="text-2xl font-bold text-gray-900">
                {apiKeys.filter(key => key.isActive).length}
              </p>
              <p className="text-xs text-gray-500">of {apiKeys.length} total</p>
            </div>
          </div>
        </div>
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
        {/* Daily Usage Chart */}
        <div className="card p-6">
          <h3 className="text-lg font-semibold mb-4">Daily API Usage</h3>
          {chartData.length > 0 ? (
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis 
                  dataKey="date" 
                  tickFormatter={(value) => new Date(value).toLocaleDateString()}
                />
                <YAxis />
                <Tooltip 
                  labelFormatter={(value) => new Date(value).toLocaleDateString()}
                />
                <Legend />
                <Line 
                  type="monotone" 
                  dataKey="requests" 
                  stroke="#3B82F6" 
                  name="Total Requests"
                  strokeWidth={2}
                />
                <Line 
                  type="monotone" 
                  dataKey="success" 
                  stroke="#10B981" 
                  name="Successful"
                  strokeWidth={2}
                />
                <Line 
                  type="monotone" 
                  dataKey="errors" 
                  stroke="#EF4444" 
                  name="Errors"
                  strokeWidth={2}
                />
              </LineChart>
            </ResponsiveContainer>
          ) : (
            <div className="flex items-center justify-center h-64 text-gray-500">
              <div className="text-center">
                <Activity className="w-12 h-12 mx-auto mb-2 opacity-50" />
                <p>No usage data available</p>
              </div>
            </div>
          )}
        </div>

        {/* API Key Usage */}
        <div className="card p-6">
          <h3 className="text-lg font-semibold mb-4">API Key Usage</h3>
          {usageStats?.apiKeyStats?.length > 0 ? (
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={usageStats.apiKeyStats}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" />
                <YAxis />
                <Tooltip />
                <Bar dataKey="total_requests" fill="#3B82F6" name="Total Requests" />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <div className="flex items-center justify-center h-64 text-gray-500">
              <div className="text-center">
                <Key className="w-12 h-12 mx-auto mb-2 opacity-50" />
                <p>No API key usage data</p>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* API Keys Summary */}
      <div className="card p-6">
        <h3 className="text-lg font-semibold mb-4">API Keys Summary</h3>
        {apiKeys.length > 0 ? (
          <div className="space-y-4">
            {apiKeys.slice(0, 5).map((apiKey) => (
              <div key={apiKey.id} className="flex items-center justify-between p-3 bg-gray-50 rounded">
                <div className="flex items-center">
                  <div className={`w-3 h-3 rounded-full mr-3 ${
                    apiKey.isActive ? 'bg-green-500' : 'bg-red-500'
                  }`}></div>
                  <div>
                    <p className="font-medium text-sm">{apiKey.name}</p>
                    <p className="text-xs text-gray-500">{apiKey.keyPrefix}</p>
                  </div>
                </div>
                <div className="text-right">
                  <p className="text-sm font-medium">
                    {apiKey.dailyLimit ? `${apiKey.dailyLimit.toLocaleString()}/day` : 'Unlimited'}
                  </p>
                  <p className="text-xs text-gray-500">
                    Last used: {apiKey.lastUsed
                      ? new Date(apiKey.lastUsed).toLocaleDateString()
                      : 'Never'
                    }
                  </p>
                </div>
              </div>
            ))}
            {apiKeys.length === 0 && (
              <p className="text-gray-500 text-center py-4">No API keys created yet</p>
            )}
          </div>
        ) : (
          <div className="text-center py-8">
            <Key className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <h4 className="text-lg font-medium text-gray-900 mb-2">No API Keys</h4>
            <p className="text-gray-600 mb-4">Create your first API key to start using the API.</p>
            <a
              href="/api-keys"
              className="btn-primary inline-flex items-center"
            >
              <Key className="w-4 h-4 mr-2" />
              Create API Key
            </a>
          </div>
        )}
      </div>
    </div>
  );
};

export default Dashboard;